// Image.js — Fully upgraded for Editor.js v2.30+
import moment from "moment";

class InstantImageTool {
  static pasteConfig = {
    files: {
      mimeTypes: ["image/*"],
    },
  };

  static get toolbox() {
    return {
      title: "Image",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M20.1673 0.332031H3.83398C2.90573 0.332031 2.01549 0.70078 1.35911 1.35716C0.702733 2.01354 0.333984 2.90377 0.333984 3.83203V20.1654C0.333984 21.0936 0.702733 21.9839 1.35911 22.6402C2.01549 23.2966 2.90573 23.6654 3.83398 23.6654H20.1673C20.3592 23.6627 20.5505 23.6432 20.739 23.607L21.089 23.5254H21.1707H21.229L21.6607 23.362L21.8123 23.2804C21.929 23.2104 22.0573 23.152 22.174 23.0704C22.3298 22.9558 22.4779 22.831 22.6173 22.697L22.699 22.592C22.8135 22.476 22.9188 22.3511 23.014 22.2187L23.119 22.067C23.2005 21.9371 23.2707 21.8005 23.329 21.6587C23.361 21.6027 23.3883 21.5442 23.4107 21.4837C23.469 21.3437 23.504 21.192 23.5507 21.0404V20.8654C23.6168 20.6374 23.656 20.4025 23.6673 20.1654V3.83203C23.6673 2.90377 23.2986 2.01354 22.6422 1.35716C21.9858 0.70078 21.0956 0.332031 20.1673 0.332031Z" fill="#373739" fill-opacity="0.7"/>
      </svg>`,
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, block }) {
    this.api = api;
    this.config = config;
    this.blockId = block?.id; 

    this.data = {
      url: data?.url || "",
      file: data?.file || null,
      size: data?.size || 1,
      position: data?.position || "center",
    };

    this.nodes = { holder: null };
    this._objectUrls = []; // Track created object URLs for cleanup

    this.settings = [
      {
        name: "Size",
        options: [0.3, 0.5, 1],
        current: this.data.size,
        onClick: (size) => {
          this.data.size = size;
          this.settings[0].current = size;
          this.updateImageStyle();
        },
      },
      {
        name: "Position",
        options: ["left", "center", "right"],
        current: this.data.position,
        onClick: (position) => {
          this.data.position = position;
          this.settings[1].current = position;
          this.updateImageStyle();
        },
      },
    ];
  }

  renderSettings() {
    const wrapper = document.createElement("div");
    this.settings.forEach((setting) => {
      const container = document.createElement("div");
      const buttonsContainer = document.createElement("div");
      buttonsContainer.classList.add("buttons-container");
      container.classList.add("settings-container");
      container.innerHTML = `<strong>${setting.name}</strong>`;

      setting.options.forEach((option) => {
        const button = document.createElement("button");
        button.innerText = option;
        button.classList.add("settings-button");
        if (option === setting.current) {
          button.classList.add("active");
        }
        button.addEventListener("click", () => {
          buttonsContainer.querySelectorAll("button").forEach((btn) => {
            btn.classList.remove("active");
          });
          button.classList.add("active");
          setting.onClick(option);
        });
        buttonsContainer.appendChild(button);
      });
      container.appendChild(buttonsContainer);
      wrapper.appendChild(container);
    });
    return wrapper;
  }

  onPaste(event) {
    if (event.type === "file") {
      const file = event.detail?.file;
      if (file && file.type.startsWith("image/")) {
        event.preventDefault?.();
        this.handleImageFile(file);
      }
    }
  }

  handleImageFile(file) {
    if (!file) return;

    const fakeUrl = `library/content/${moment().valueOf()}_${file.name}`;
    this.data = {
      file: file,
      url: fakeUrl,
      size: this.data.size,
      position: this.data.position,
    };

    const previewUrl = URL.createObjectURL(file);
    this._objectUrls.push(previewUrl);

    this.displayImage(previewUrl);
    this.syncBlockWithData();
  }

  renderEmptyState() {
    const container = document.createElement("div");
    container.classList.add("instant-image-tool-empty");

    const uploadButton = document.createElement("button");
    uploadButton.type = "button";
    uploadButton.classList.add("image-upload-button");
    uploadButton.textContent = "Upload Image";

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.style.display = "none";

    uploadButton.addEventListener("click", (e) => {
      e.stopPropagation();
      fileInput.click();
    });

    fileInput.addEventListener("change", (e) => {
      const file = fileInput.files[0];
      if (file) this.handleImageFile(file);
    });

    container.addEventListener("dragover", (e) => {
      e.preventDefault();
      container.classList.add("dragover");
    });
    container.addEventListener("dragleave", () => {
      container.classList.remove("dragover");
    });
    container.addEventListener("drop", (e) => {
      e.preventDefault();
      container.classList.remove("dragover");
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith("image/")) {
        this.handleImageFile(file);
      }
    });

    container.appendChild(uploadButton);
    container.appendChild(fileInput);
    return container;
  }

  render() {
    this.wrapper = document.createElement("div");
    this.wrapper.classList.add("instant-image-tool");

    if (!this.data.url && !this.data.file) {
      const emptyState = this.renderEmptyState();
      this.wrapper.appendChild(emptyState);
    } else {
      const loadingDiv = document.createElement("div");
      loadingDiv.classList.add("image-loading-placeholder");
      loadingDiv.textContent = "Loading image...";
      this.wrapper.appendChild(loadingDiv);

      this.loadImageSrc()
        .then((src) => {
          this.wrapper.innerHTML = "";
          const img = document.createElement("img");
          img.src = src;
          img.classList.add("uploaded-image");
          this.applyImageStyles(img);
          img.addEventListener("click", () => {
            if (document.fullscreenElement) {
              document.exitFullscreen();
            } else {
              img.requestFullscreen();
            }
          });
          this.wrapper.appendChild(img);
        })
        .catch((error) => {
          console.error("Failed to load image:", error);
          this.wrapper.innerHTML = `<div class="image-error">Failed to load image</div>`;
        });
    }

    this.nodes.holder = this.wrapper;
    return this.nodes.holder;
  }

  async loadImageSrc() {
    if (this.data.file instanceof File) {
      const objectUrl = URL.createObjectURL(this.data.file);
      this._objectUrls.push(objectUrl);
      return objectUrl;
    }

    if (this.data.url?.startsWith("library/content/")) {
      const fileName = this.data.url.split("/").pop();
      const result = this.config.getResult?.();
      const existing = result?.find((r) => r?.name === fileName);
      if (existing?.link) return existing.link;

      try {
        const response = await this.config.getPreSignedUrl(
          `library/content/${this.config.qnid}/${fileName}`,
          undefined,
          fileName.split(".").pop(),
          "GET"
        );
        return response.url;
      } catch (error) {
        console.error("Failed to get pre-signed URL:", error);
        throw error;
      }
    }

    return this.data.url;
  }

  displayImage(src) {
    this.wrapper.innerHTML = "";
    const img = document.createElement("img");
    img.src = src;
    img.classList.add("uploaded-image");
    this.applyImageStyles(img);
    img.addEventListener("click", () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        img.requestFullscreen();
      }
    });
    this.wrapper.appendChild(img);
  }

  applyImageStyles(img) {
    const widthPct = this.data.size * 100;
    const remaining = 100 - widthPct;
    img.style.width = `${widthPct}%`;
    img.style.height = "auto";
    img.style.display = "block";

    if (this.data.position === "left") {
      img.style.marginLeft = "0";
      img.style.marginRight = `${remaining}%`;
    } else if (this.data.position === "right") {
      img.style.marginLeft = `${remaining}%`;
      img.style.marginRight = "0";
    } else {
      img.style.marginLeft = `${remaining / 2}%`;
      img.style.marginRight = `${remaining / 2}%`;
    }
  }

  updateImageStyle() {
    const img = this.nodes.holder?.querySelector("img");
    if (img) {
      this.applyImageStyles(img);
    }
    this.syncBlockWithData();
  }

  syncBlockWithData() {
    if (this.blockId) {
      this.api.blocks.update(this.blockId, this.data);
    } else {
      console.warn("Block ID not available; update skipped.");
    }
  }

  save() {
    return {
      url: this.data.url,
      file: this.data.file,
      size: this.data.size,
      position: this.data.position,
    };
  }

  destroy() {
    this._objectUrls.forEach((url) => URL.revokeObjectURL(url));
    this._objectUrls = [];
    console.log("InstantImageTool destroyed, object URLs revoked");
  }
}

export default InstantImageTool;